/**
 * Loading placeholders.
 *
 * A skeleton is a shimmering block on `surface-sunken`; the sheen is a gradient
 * built from `border`, which sits on the correct side of the track in both
 * themes. The blocks themselves are `aria-hidden` — only the composite
 * skeletons (card, table) announce a single "Loading…" status, so a screen
 * reader hears one message instead of twenty empty boxes.
 */
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export type SkeletonRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

const RADIUS_CLASS: Record<SkeletonRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full',
};

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  rounded?: SkeletonRadius;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { className, rounded = 'md', ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn('relative overflow-hidden bg-surface-sunken', RADIUS_CLASS[rounded], className)}
      {...props}
    >
      <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-border/80 to-transparent" />
    </div>
  );
});

export interface SkeletonTextProps extends HTMLAttributes<HTMLDivElement> {
  /** Number of lines; the last one is short so it reads like a paragraph. */
  lines?: number;
}

export const SkeletonText = forwardRef<HTMLDivElement, SkeletonTextProps>(function SkeletonText(
  { className, lines = 3, ...props },
  ref,
) {
  const count = Math.max(1, lines);

  return (
    <div ref={ref} aria-hidden="true" className={cn('flex flex-col gap-2', className)} {...props}>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3.5 w-full', index === count - 1 && count > 1 && 'w-3/5')}
        />
      ))}
    </div>
  );
});

export interface SkeletonCardProps extends HTMLAttributes<HTMLDivElement> {
  lines?: number;
  /** Renders a circular avatar placeholder in the header. */
  withAvatar?: boolean;
  label?: string;
}

export const SkeletonCard = forwardRef<HTMLDivElement, SkeletonCardProps>(function SkeletonCard(
  { className, lines = 3, withAvatar = true, label = 'Loading…', ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      role="status"
      aria-busy="true"
      className={cn('rounded-xl border border-border bg-surface p-5 shadow-card', className)}
      {...props}
    >
      <div className="flex items-center gap-3" aria-hidden="true">
        {withAvatar ? <Skeleton rounded="full" className="h-10 w-10 shrink-0" /> : null}
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-3 w-1/4" />
        </div>
      </div>
      <SkeletonText lines={lines} className="mt-4" />
      <span className="sr-only">{label}</span>
    </div>
  );
});

export interface SkeletonTableProps extends HTMLAttributes<HTMLDivElement> {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  label?: string;
}

export const SkeletonTable = forwardRef<HTMLDivElement, SkeletonTableProps>(function SkeletonTable(
  { className, rows = 5, columns = 4, showHeader = true, label = 'Loading table…', ...props },
  ref,
) {
  const rowCount = Math.max(1, rows);
  const columnCount = Math.max(1, columns);
  const gridStyle = { gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` };

  return (
    <div ref={ref} role="status" aria-busy="true" className={cn('w-full', className)} {...props}>
      {showHeader ? (
        <div className="grid gap-4 bg-surface-sunken px-3 py-2.5" style={gridStyle} aria-hidden="true">
          {Array.from({ length: columnCount }, (_, index) => (
            <Skeleton key={index} className="h-3 w-2/3" />
          ))}
        </div>
      ) : null}

      {Array.from({ length: rowCount }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="grid gap-4 border-t border-border px-3 py-3.5"
          style={gridStyle}
          aria-hidden="true"
        >
          {Array.from({ length: columnCount }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 0 ? 'w-4/5' : 'w-3/5')}
            />
          ))}
        </div>
      ))}

      <span className="sr-only">{label}</span>
    </div>
  );
});
